/**
 * The install instructions, generated in exactly one place.
 *
 * The dashboard's install screen, the admin API's /snippet endpoint and the
 * ingest Worker's /install.md all render from these functions. If they were
 * written out three times they would drift, and an agent following one of them
 * would install something subtly different from what a human copied from
 * another. TASKS.md has an acceptance criterion asserting they stay identical.
 */
import type { IdentityMode } from './schema';

/** The one line every site needs. */
export function scriptTag(siteKey: string, ingestOrigin: string): string {
  return `<script defer src="${ingestOrigin}/t.js" data-site="${siteKey}"></script>`;
}

/**
 * The one call an identified site needs, once the user is authenticated.
 *
 * It uses a command queue — the same pattern as Google Analytics' dataLayer —
 * because the tracker is loaded with `defer` and an app may well call this
 * before it has run. A method on a global would throw in that case; pushing
 * onto an array works whether the tracker has loaded yet or not.
 */
export const IDENTIFY_CALL = "(window.totumq = window.totumq || []).push(['identify', user.sub, { account: user.organisationId }]);";

/** A custom event, in the same always-safe form. */
export const TRACK_CALL_EXAMPLE = "(window.totumq = window.totumq || []).push(['track', 'report_generated', { type: 'assessment' }]);";

export interface Snippet {
  scriptTag: string;
  /** Present only for identified sites. */
  identifyCall: string | null;
  /** The plain-text install guide a coding agent can fetch and follow. */
  installGuide: string;
}

export function snippetFor(siteKey: string, ingestOrigin: string, identityMode: IdentityMode): Snippet {
  return {
    scriptTag: scriptTag(siteKey, ingestOrigin),
    identifyCall: identityMode === 'identified' ? IDENTIFY_CALL : null,
    installGuide: `${ingestOrigin}/install.md`,
  };
}

/**
 * Plain-text install guide served at /install.md, written for a coding agent
 * to fetch and follow without a human pasting documentation into its prompt.
 * Deliberately prescriptive: one way to install, no alternatives offered.
 */
export function installMarkdown(ingestOrigin: string): string {
  const tag = scriptTag('<SITE_KEY>', ingestOrigin);
  return `# Installing Totum Analytics

There is exactly one way to install this. Follow it as written.

## 1. Add the script tag

Add this line inside the \`<head>\` of every page, or once in the root HTML
template of a single-page app. Replace \`<SITE_KEY>\` with the key shown for the
site at https://analytics.totumcloud.com (it looks like \`tot_\` followed by 12
characters).

\`\`\`html
${tag}
\`\`\`

Do not add \`async\`. Do not move it to the end of the body. Do not bundle or
self-host the script. \`defer\` is what keeps it off the critical path.

## 2. Identified sites only: call identify after login

If the site is set to identified mode in the dashboard, call this once the user
is authenticated — after login completes, and on every page load where the user
is already logged in. Use the Cognito \`sub\` as the user reference and the
organisation id as the account. Pass ids only, never names or email addresses.

\`\`\`js
${IDENTIFY_CALL}
\`\`\`

This is safe whether or not the script has loaded yet: it pushes onto a queue
the tracker drains when it starts. Do not replace it with a direct method call.

For a server-rendered page that already knows the user, the equivalent is a
\`data-user-id\` attribute on the script tag.

Anonymous sites must not call identify. The ingest endpoint discards user
references from anonymous sites.

## 3. Optional: custom events

\`\`\`js
${TRACK_CALL_EXAMPLE}
\`\`\`

Event names are lowercase with \`_ . : -\` allowed. Custom events are what the
feature adoption reports are built from, so name them after the feature, not the
button.

## 4. Verify

Load a page on the site, then check
\`GET https://analytics-api.totumcloud.com/sites/<SITE_ID>/install-status\` or the
install screen in the dashboard. It reports whether events are arriving and, for
identified sites, whether any of them carry a user reference yet.

Add \`data-debug="true"\` to the script tag to log every beacon to the browser
console while you check. Remove it afterwards.

## What not to do

- Do not use cookies, localStorage or sessionStorage to help the tracker. It
  stores nothing on the device by design, and that is why no consent banner is
  needed.
- Do not send personal data in event properties.
- Do not install it twice on one page.
`;
}
